import { FLORIDA_CORRIDOR_SLUGS } from "./data/floridaCorridorSlugs";
import { TEXAS_CORRIDOR_SLUGS } from "./data/texasCorridorSlugs";
import { CALIFORNIA_CORRIDOR_SLUGS } from "./data/californiaCorridorSlugs";
import { COLORADO_CORRIDOR_SLUGS } from "./data/coloradoCorridorSlugs";
import { ARIZONA_CORRIDOR_SLUGS } from "./data/arizonaCorridorSlugs";
import { NEW_YORK_CORRIDOR_SLUGS } from "./data/newYorkCorridorSlugs";
import { SUBURB_CORRIDOR_SLUGS } from "./data/suburbCorridorSlugs";
import { DESTINATION_GUIDE_SLUGS } from "./data/destinationGuideSlugs";
import { lazy, Suspense, ComponentType, Component, ReactNode, useEffect } from "react";
import { sweepStaleDrafts } from "@/lib/formDraftStorage";

// Helper to retry lazy imports on chunk load failure (common after deployments)
// Uses sessionStorage to prevent infinite reload loops and only logs to Sentry after max retries
const CHUNK_RELOAD_KEY = 'chunk_reload_count';
const MAX_CHUNK_RETRIES = 2;

const lazyWithRetry = <T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      // Reset reload count on successful load
      const result = await componentImport();
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return result;
    } catch (error) {
      // Track reload attempts to prevent infinite loops
      const reloadCount = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);

      if (reloadCount < MAX_CHUNK_RETRIES) {
        // Increment counter and reload to pick up fresh chunk hashes after a deploy.
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(reloadCount + 1));
        // Kick off the reload immediately. Use replace() so back-button doesn't loop.
        try { window.location.reload(); } catch { /* noop */ }
        // Return a neutral "Updating…" placeholder so users don't see a flashed 404
        // while the browser is tearing down the page for the reload.
        const Placeholder = (() => (
          <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            color: '#475569',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: 14,
          }}>
            Updating to the latest version…
          </div>
        )) as unknown as T;
        return { default: Placeholder };
      }

      // Max retries exceeded - clear counter and let the error propagate
      // This will be caught by Sentry's error boundary
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      throw error;
    }
  });
const Toaster = lazyWithRetry(() =>
  import("@/components/ui/toaster").then(m => ({ default: m.Toaster }))
);
const Sonner = lazyWithRetry(() =>
  import("@/components/ui/sonner").then(m => ({ default: m.Toaster }))
);
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ExitIntentOffer from "@/components/conversion/ExitIntentOffer";
import { GTMRouteTracker } from "@/components/GTMRouteTracker";
import { MetaPixelRouteTracker } from "@/components/MetaPixelRouteTracker";
import { GlobalMobileCTA } from "@/components/conversion/GlobalMobileCTA";
import { ClickToCallTracker } from "@/components/conversion/ClickToCallTracker";

const AdminLayout = lazyWithRetry(() =>
  import("./components/AdminLayout").then(m => ({ default: m.AdminLayout }))
);
const ProtectedRoute = lazyWithRetry(() =>
  import("./components/ProtectedRoute").then(m => ({ default: m.ProtectedRoute }))
);
import { ErrorFallback } from "./components/ErrorFallback";
import { ScrollToTop } from "./components/ScrollToTop";
const ChatWidgetWrapper = lazyWithRetry(() => import("@/components/vibe/ChatWidgetWrapper"));
const FormerIndex = lazyWithRetry(() => import("./pages/former/FormerIndex"));
const FormerMoverPage = lazyWithRetry(() => import("./pages/former/FormerMoverPage"));
const AutoShippingQuotes = lazyWithRetry(() => import("./pages/AutoShippingQuotes"));
const AdminRuntime = lazyWithRetry(() =>
  import("@/components/admin/AdminRuntime").then(m => ({ default: m.AdminRuntime }))
);
const BookingProviderWrapper = lazyWithRetry(() =>
  import("./components/booking/BookingProviderWrapper").then(m => ({ default: m.BookingProviderWrapper }))
);
const CRMPanelWrapper = lazyWithRetry(() =>
  import("./components/crm/CRMPanelWrapper").then(m => ({ default: m.CRMPanelWrapper }))
);
// Deferred - analytics, doesn't need to block first paint
const VisitorPresenceTracker = lazyWithRetry(() =>
  import("@/components/VisitorPresenceTracker").then(m => ({ default: m.VisitorPresenceTracker }))
);
const GoogleAnalyticsTracker = lazyWithRetry(() =>
  import("@/components/GoogleAnalyticsTracker").then(m => ({ default: m.GoogleAnalyticsTracker }))
);
const PageAnalyticsTracker = lazyWithRetry(() =>
  import("@/components/PageAnalyticsTracker").then(m => ({ default: m.PageAnalyticsTracker }))
);
// Conditionally loaded - only mount when URL matches legacy patterns
const ElementsKitRedirect = lazyWithRetry(() =>
  import("@/components/ElementsKitRedirect").then(m => ({ default: m.ElementsKitRedirect }))
);
const TrailingSlashRedirect = lazyWithRetry(() =>
  import("@/components/TrailingSlashRedirect").then(m => ({ default: m.TrailingSlashRedirect }))
);
const LegacyPathRedirect = lazyWithRetry(() =>
  import("@/components/LegacyPathRedirect").then(m => ({ default: m.LegacyPathRedirect }))
);
import { RedirectHandler } from "@/components/RedirectHandler";

// Lazy load all pages for better performance (with retry on chunk load failure)
const Index = lazyWithRetry(() => import("./pages/Index"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const CustomerPortal = lazyWithRetry(() => import("./pages/CustomerPortal"));
const Quote = lazyWithRetry(() => import("./pages/Quote"));
const ResumeBooking = lazyWithRetry(() => import("./pages/ResumeBooking"));
const CustomerJobInventory = lazyWithRetry(() => import("./pages/job/CustomerJobInventory"));
const CustomerPayment = lazyWithRetry(() => import("./pages/CustomerPayment"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const ServiceAreas = lazyWithRetry(() => import("./pages/ServiceAreas"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const About = lazyWithRetry(() => import("./pages/About"));
const TermsAndConditions = lazyWithRetry(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Sitemap = lazyWithRetry(() => import("./pages/Sitemap"));

// Service pages
const ResidentialMoving = lazyWithRetry(() => import("./pages/services/ResidentialMoving"));
const ApartmentMoving = lazyWithRetry(() => import("./pages/services/ApartmentMoving"));
const CommercialMoving = lazyWithRetry(() => import("./pages/services/CommercialMoving"));
const FullServiceMoving = lazyWithRetry(() => import("./pages/services/FullServiceMoving"));
const HouseholdMoving = lazyWithRetry(() => import("./pages/services/HouseholdMoving"));
const InterstateMove = lazyWithRetry(() => import("./pages/services/InterstateMove"));
const LocalMove = lazyWithRetry(() => import("./pages/services/LocalMove"));
const LongDistanceMove = lazyWithRetry(() => import("./pages/services/LongDistanceMove"));
const CrossCountryMoving = lazyWithRetry(() => import("./pages/services/CrossCountryMoving"));
const SameDayMoving = lazyWithRetry(() => import("./pages/services/SameDayMoving"));
const PackingService = lazyWithRetry(() => import("./pages/services/PackingService"));
const StorageServices = lazyWithRetry(() => import("./pages/services/StorageServices"));
const SeniorMoversService = lazyWithRetry(() => import("./pages/services/SeniorMovers"));
const PianoMovers = lazyWithRetry(() => import("./pages/services/PianoMovers"));
const MovingSupplies = lazyWithRetry(() => import("./pages/services/MovingSupplies"));
const NationalMoving = lazyWithRetry(() => import("./pages/services/NationalMoving"));
const OfficeMove = lazyWithRetry(() => import("./pages/services/OfficeMove"));
const MovingWithKids = lazyWithRetry(() => import("./pages/services/MovingWithKids"));
const CompetitivePricing = lazyWithRetry(() => import("./pages/services/CompetitivePricing"));
const QuickPickup = lazyWithRetry(() => import("./pages/services/QuickPickup"));
const EmergencyMove = lazyWithRetry(() => import("./pages/services/EmergencyMove"));

// Public Reviews page
const LifetimeMoversReviews = lazyWithRetry(() => import("./pages/LifetimeMoversReviews"));
const LifetimeMovingCompany = lazyWithRetry(() => import("./pages/LifetimeMovingCompany"));
const LifetimeMoving = lazyWithRetry(() => import("./pages/LifetimeMoving"));
const LifetimeMovingAndStorage = lazyWithRetry(() => import("./pages/LifetimeMovingAndStorage"));
const LifetimeMovingCo = lazyWithRetry(() => import("./pages/LifetimeMovingCo"));
const AboutLifetimeMovers = lazyWithRetry(() => import("./pages/AboutLifetimeMovers"));

// Location pages
const MoversAnaheim = lazyWithRetry(() => import("./pages/locations/MoversAnaheim"));
const MoversArlington = lazyWithRetry(() => import("./pages/locations/MoversArlington"));
const MoversAustin = lazyWithRetry(() => import("./pages/locations/MoversAustin"));
const MoversBakersfield = lazyWithRetry(() => import("./pages/locations/MoversBakersfield"));
const MoversBarrington = lazyWithRetry(() => import("./pages/locations/MoversBarrington"));
const MoversBucktown = lazyWithRetry(() => import("./pages/locations/MoversBucktown"));
const MoversAvondale = lazyWithRetry(() => import("./pages/locations/MoversAvondale"));
const MoversLoop = lazyWithRetry(() => import("./pages/locations/MoversLoop"));
const MoversCicero = lazyWithRetry(() => import("./pages/locations/MoversCicero"));
const MoversUkrainianVillage = lazyWithRetry(() => import("./pages/locations/MoversUkrainianVillage"));
const MoversHumboldtPark = lazyWithRetry(() => import("./pages/locations/MoversHumboldtPark"));
const MoversRogersPark = lazyWithRetry(() => import("./pages/locations/MoversRogersPark"));
const MoversEdgebrook = lazyWithRetry(() => import("./pages/locations/MoversEdgebrook"));
const MoversWrigleyville = lazyWithRetry(() => import("./pages/locations/MoversWrigleyville"));
const MoversGurnee = lazyWithRetry(() => import("./pages/locations/MoversGurnee"));
const MoversWestLoop = lazyWithRetry(() => import("./pages/locations/MoversWestLoop"));
const MoversChicago = lazyWithRetry(() => import("./pages/locations/MoversChicago"));
const MoversDallas = lazyWithRetry(() => import("./pages/locations/MoversDallas"));
const MoversLosAngeles = lazyWithRetry(() => import("./pages/locations/MoversLosAngeles"));
const MoversNaperville = lazyWithRetry(() => import("./pages/locations/MoversNaperville"));
const MoversWilmette = lazyWithRetry(() => import("./pages/locations/MoversWilmette"));
const MoversWinnetka = lazyWithRetry(() => import("./pages/locations/MoversWinnetka"));
const MoversEvanston = lazyWithRetry(() => import("./pages/locations/MoversEvanston"));
const MoversOakPark = lazyWithRetry(() => import("./pages/locations/MoversOakPark"));
const MoversSchaumburg = lazyWithRetry(() => import("./pages/locations/MoversSchaumburg"));
const MoversHoffmanEstates = lazyWithRetry(() => import("./pages/locations/MoversHoffmanEstates"));
const MoversDownersGrove = lazyWithRetry(() => import("./pages/locations/MoversDownersGrove"));
const MoversAurora = lazyWithRetry(() => import("./pages/locations/MoversAurora"));
const MoversJoliet = lazyWithRetry(() => import("./pages/locations/MoversJoliet"));
const MoversRockford = lazyWithRetry(() => import("./pages/locations/MoversRockford"));
const MoversDeKalb = lazyWithRetry(() => import("./pages/locations/MoversDeKalb"));
const MoversHighlandPark = lazyWithRetry(() => import("./pages/locations/MoversHighlandPark"));
const MoversWheaton = lazyWithRetry(() => import("./pages/locations/MoversWheaton"));
const LocationPage = lazyWithRetry(() => import("./pages/LocationPage"));
const MoversVernonHills = lazyWithRetry(() => import("./pages/locations/MoversVernonHills"));
const MoversNorthbrook = lazyWithRetry(() => import("./pages/locations/MoversNorthbrook"));
const MoversIndianapolis = lazyWithRetry(() => import("./pages/locations/MoversIndianapolis"));
const MoversFortWayne = lazyWithRetry(() => import("./pages/locations/MoversFortWayne"));
const MoversCarmel = lazyWithRetry(() => import("./pages/locations/MoversCarmel"));
const MoversFishers = lazyWithRetry(() => import("./pages/locations/MoversFishers"));
const MoversBloomington = lazyWithRetry(() => import("./pages/locations/MoversBloomington"));
const MoversSouthBend = lazyWithRetry(() => import("./pages/locations/MoversSouthBend"));
const MoversEvansville = lazyWithRetry(() => import("./pages/locations/MoversEvansville"));
const MoversHammond = lazyWithRetry(() => import("./pages/locations/MoversHammond"));
const MoversGary = lazyWithRetry(() => import("./pages/locations/MoversGary"));
const MoversLafayette = lazyWithRetry(() => import("./pages/locations/MoversLafayette"));
const MoversMilwaukee = lazyWithRetry(() => import("./pages/locations/MoversMilwaukee"));
const MoversMadison = lazyWithRetry(() => import("./pages/locations/MoversMadison"));
const MoversGreenBay = lazyWithRetry(() => import("./pages/locations/MoversGreenBay"));
const MoversKenosha = lazyWithRetry(() => import("./pages/locations/MoversKenosha"));
const MoversRacine = lazyWithRetry(() => import("./pages/locations/MoversRacine"));
const MoversAppleton = lazyWithRetry(() => import("./pages/locations/MoversAppleton"));
const MoversWaukesha = lazyWithRetry(() => import("./pages/locations/MoversWaukesha"));
const MoversHinsdale = lazyWithRetry(() => import("./pages/locations/MoversHinsdale"));
const MoversLakeBarrington = lazyWithRetry(() => import("./pages/locations/MoversLakeBarrington"));
const MoversGoldCoast = lazyWithRetry(() => import("./pages/locations/MoversGoldCoast"));
// Inbox, SentEmailsList, ResponseRules, ResponseTemplates moved to AdminRoutes.tsx
const MoversEauClaire = lazyWithRetry(() => import("./pages/locations/MoversEauClaire"));
const MoversDetroit = lazyWithRetry(() => import("./pages/locations/MoversDetroit"));
const MoversGrandRapids = lazyWithRetry(() => import("./pages/locations/MoversGrandRapids"));
const MoversAnnArbor = lazyWithRetry(() => import("./pages/locations/MoversAnnArbor"));
const MoversLansing = lazyWithRetry(() => import("./pages/locations/MoversLansing"));
const MoversSterlingHeights = lazyWithRetry(() => import("./pages/locations/MoversSterlingHeights"));
const MoversFlint = lazyWithRetry(() => import("./pages/locations/MoversFlint"));
const MoversTraverseCity = lazyWithRetry(() => import("./pages/locations/MoversTraverseCity"));
const MoversKalamazoo = lazyWithRetry(() => import("./pages/locations/MoversKalamazoo"));
const MoversHouston = lazyWithRetry(() => import("./pages/locations/MoversHouston"));
const MoversSanAntonio = lazyWithRetry(() => import("./pages/locations/MoversSanAntonio"));
const MoversSanDiego = lazyWithRetry(() => import("./pages/locations/MoversSanDiego"));
const MoversSanFrancisco = lazyWithRetry(() => import("./pages/locations/MoversSanFrancisco"));
const MoversIrvine = lazyWithRetry(() => import("./pages/locations/MoversIrvine"));
const MoversFairfield = lazyWithRetry(() => import("./pages/locations/MoversFairfield"));
const MoversLubbock = lazyWithRetry(() => import("./pages/locations/MoversLubbock"));
const MoversStreeterville = lazyWithRetry(() => import("./pages/locations/MoversStreeterville"));
const MoversSanJose = lazyWithRetry(() => import("./pages/locations/MoversSanJose"));
const MoversSacramento = lazyWithRetry(() => import("./pages/locations/MoversSacramento"));
const MoversFresno = lazyWithRetry(() => import("./pages/locations/MoversFresno"));
const MoversAtlanta = lazyWithRetry(() => import("./pages/locations/MoversAtlanta"));
const MoversGlenEllyn = lazyWithRetry(() => import("./pages/locations/MoversGlenEllyn"));
const MoversCharlotte = lazyWithRetry(() => import("./pages/locations/MoversCharlotte"));
const MoversRaleigh = lazyWithRetry(() => import("./pages/locations/MoversRaleigh"));
const MoversMiami = lazyWithRetry(() => import("./pages/locations/MoversMiami"));
const MoversTampa = lazyWithRetry(() => import("./pages/locations/MoversTampa"));
const MoversOrlando = lazyWithRetry(() => import("./pages/locations/MoversOrlando"));
const MoversJacksonville = lazyWithRetry(() => import("./pages/locations/MoversJacksonville"));
const MoversNashville = lazyWithRetry(() => import("./pages/locations/MoversNashville"));
const MoversMemphis = lazyWithRetry(() => import("./pages/locations/MoversMemphis"));
const MoversLouisville = lazyWithRetry(() => import("./pages/locations/MoversLouisville"));
const MoversColumbus = lazyWithRetry(() => import("./pages/locations/MoversColumbus"));
const MoversCleveland = lazyWithRetry(() => import("./pages/locations/MoversCleveland"));
const MoversCincinnati = lazyWithRetry(() => import("./pages/locations/MoversCincinnati"));
const MoversPittsburgh = lazyWithRetry(() => import("./pages/locations/MoversPittsburgh"));
const MoversPhiladelphia = lazyWithRetry(() => import("./pages/locations/MoversPhiladelphia"));
const MoversBoston = lazyWithRetry(() => import("./pages/locations/MoversBoston"));
const MoversNewYork = lazyWithRetry(() => import("./pages/locations/MoversNewYork"));
const MoversBaltimore = lazyWithRetry(() => import("./pages/locations/MoversBaltimore"));
const MoversMinneapolis = lazyWithRetry(() => import("./pages/locations/MoversMinneapolis"));
const MoversStLouis = lazyWithRetry(() => import("./pages/locations/MoversStLouis"));
const MoversKansasCity = lazyWithRetry(() => import("./pages/locations/MoversKansasCity"));
const MoversOmaha = lazyWithRetry(() => import("./pages/locations/MoversOmaha"));
const MoversOklahomaCity = lazyWithRetry(() => import("./pages/locations/MoversOklahomaCity"));
const MoversTulsa = lazyWithRetry(() => import("./pages/locations/MoversTulsa"));
const MoversAlbuquerque = lazyWithRetry(() => import("./pages/locations/MoversAlbuquerque"));
const MoversTucson = lazyWithRetry(() => import("./pages/locations/MoversTucson"));
const MoversPhoenix = lazyWithRetry(() => import("./pages/locations/MoversPhoenix"));
const MoversSeattle = lazyWithRetry(() => import("./pages/locations/MoversSeattle"));
const MoversDenver = lazyWithRetry(() => import("./pages/locations/MoversDenver"));
const MoversPortland = lazyWithRetry(() => import("./pages/locations/MoversPortland"));
const MoversLasVegas = lazyWithRetry(() => import("./pages/locations/MoversLasVegas"));
const MoversLakeForest = lazyWithRetry(() => import("./pages/locations/MoversLakeForest"));
const MoversLincolnPark = lazyWithRetry(() => import("./pages/locations/MoversLincolnPark"));
const MoversGeneva = lazyWithRetry(() => import("./pages/locations/MoversGeneva"));
const MoversElmhurst = lazyWithRetry(() => import("./pages/locations/MoversElmhurst"));
const MoversNorthfield = lazyWithRetry(() => import("./pages/locations/MoversNorthfield"));
const MoversLakeview = lazyWithRetry(() => import("./pages/locations/MoversLakeview"));
const MoversOldTown = lazyWithRetry(() => import("./pages/locations/MoversOldTown"));
const MoversDeerPark = lazyWithRetry(() => import("./pages/locations/MoversDeerPark"));
const MoversKildeer = lazyWithRetry(() => import("./pages/locations/MoversKildeer"));
const MoversHomerGlen = lazyWithRetry(() => import("./pages/locations/MoversHomerGlen"));
const MoversOakBrook = lazyWithRetry(() => import("./pages/locations/MoversOakBrook"));
const MoversSouthLoop = lazyWithRetry(() => import("./pages/locations/MoversSouthLoop"));
const MoversLombard = lazyWithRetry(() => import("./pages/locations/MoversLombard"));
const MoversDeerfield = lazyWithRetry(() => import("./pages/locations/MoversDeerfield"));
const MoversCountryClubHills = lazyWithRetry(() => import("./pages/locations/MoversCountryClubHills"));
const MoversParkRidge = lazyWithRetry(() => import("./pages/locations/MoversParkRidge"));
const MoversAlbanyPark = lazyWithRetry(() => import("./pages/locations/MoversAlbanyPark"));
const MoversStreamwood = lazyWithRetry(() => import("./pages/locations/MoversStreamwood"));
const MoversRiverGrove = lazyWithRetry(() => import("./pages/locations/MoversRiverGrove"));
const MoversRiverForest = lazyWithRetry(() => import("./pages/locations/MoversRiverForest"));
const MoversPalatine = lazyWithRetry(() => import("./pages/locations/MoversPalatine"));
const MoversNorwoodPark = lazyWithRetry(() => import("./pages/locations/MoversNorwoodPark"));
const MoversMinooka = lazyWithRetry(() => import("./pages/locations/MoversMinooka"));
const MoversWoodridge = lazyWithRetry(() => import("./pages/locations/MoversWoodridge"));
const MoversLoganSquare = lazyWithRetry(() => import("./pages/locations/MoversLoganSquare"));
const MoversBolingbrook = lazyWithRetry(() => import("./pages/locations/MoversBolingbrook"));
const MoversChannahon = lazyWithRetry(() => import("./pages/locations/MoversChannahon"));
const MoversNorthChicago = lazyWithRetry(() => import("./pages/locations/MoversNorthChicago"));
const MoversLibertyville = lazyWithRetry(() => import("./pages/locations/MoversLibertyville"));
const MoversHawthornWoods = lazyWithRetry(() => import("./pages/locations/MoversHawthornWoods"));
const MoversMonee = lazyWithRetry(() => import("./pages/locations/MoversMonee"));
const MoversBeecher = lazyWithRetry(() => import("./pages/locations/MoversBeecher"));
const MoversHodgkins = lazyWithRetry(() => import("./pages/locations/MoversHodgkins"));
const MoversWesternSprings = lazyWithRetry(() => import("./pages/locations/MoversWesternSprings"));
const MoversLemont = lazyWithRetry(() => import("./pages/locations/MoversLemont"));
const MoversPilsen = lazyWithRetry(() => import("./pages/locations/MoversPilsen"));
const MoversRollingMeadows = lazyWithRetry(() => import("./pages/locations/MoversRollingMeadows"));
const MoversMcHenry = lazyWithRetry(() => import("./pages/locations/MoversMcHenry"));
const MoversMorris = lazyWithRetry(() => import("./pages/locations/MoversMorris"));
const MoversBridgeview = lazyWithRetry(() => import("./pages/locations/MoversBridgeview"));
const MoversBurrRidge = lazyWithRetry(() => import("./pages/locations/MoversBurrRidge"));
const MoversPingreeGrove = lazyWithRetry(() => import("./pages/locations/MoversPingreeGrove"));
const MoversOakbrookTerrace = lazyWithRetry(() => import("./pages/locations/MoversOakbrookTerrace"));
const MoversVillaPark = lazyWithRetry(() => import("./pages/locations/MoversVillaPark"));
const MoversWestmont = lazyWithRetry(() => import("./pages/locations/MoversWestmont"));
const MoversWillowbrook = lazyWithRetry(() => import("./pages/locations/MoversWillowbrook"));
const MoversLisle = lazyWithRetry(() => import("./pages/locations/MoversLisle"));
const MoversClarendonHills = lazyWithRetry(() => import("./pages/locations/MoversClarendonHills"));
const MoversBrookfield = lazyWithRetry(() => import("./pages/locations/MoversBrookfield"));
const MoversBerwyn = lazyWithRetry(() => import("./pages/locations/MoversBerwyn"));
const MoversRiverside = lazyWithRetry(() => import("./pages/locations/MoversRiverside"));
const MoversQuincy = lazyWithRetry(() => import("./pages/locations/MoversQuincy"));
const MoversForestPark = lazyWithRetry(() => import("./pages/locations/MoversForestPark"));
const MoversCarolStream = lazyWithRetry(() => import("./pages/locations/MoversCarolStream"));
const MoversAddison = lazyWithRetry(() => import("./pages/locations/MoversAddison"));
const MoversItasca = lazyWithRetry(() => import("./pages/locations/MoversItasca"));
const MoversRoselle = lazyWithRetry(() => import("./pages/locations/MoversRoselle"));
const MoversDarien = lazyWithRetry(() => import("./pages/locations/MoversDarien"));
const MoversElkGroveVillage = lazyWithRetry(() => import("./pages/locations/MoversElkGroveVillage"));
const MoversDesPlaines = lazyWithRetry(() => import("./pages/locations/MoversDesPlaines"));
const MoversMountProspect = lazyWithRetry(() => import("./pages/locations/MoversMountProspect"));
const MoversArlingtonHeights = lazyWithRetry(() => import("./pages/locations/MoversArlingtonHeights"));
const MoversSkokie = lazyWithRetry(() => import("./pages/locations/MoversSkokie"));
const MoversOrlandPark = lazyWithRetry(() => import("./pages/locations/MoversOrlandPark"));
const MoversGlencoe = lazyWithRetry(() => import("./pages/locations/MoversGlencoe"));
const MoversLincolnshire = lazyWithRetry(() => import("./pages/locations/MoversLincolnshire"));
const MoversCrestwood = lazyWithRetry(() => import("./pages/locations/MoversCrestwood"));
const MoversHazelCrest = lazyWithRetry(() => import("./pages/locations/MoversHazelCrest"));
const MoversHickoryHills = lazyWithRetry(() => import("./pages/locations/MoversHickoryHills"));
const MoversHometown = lazyWithRetry(() => import("./pages/locations/MoversHometown"));
const MoversJustice = lazyWithRetry(() => import("./pages/locations/MoversJustice"));
const MoversOakLawn = lazyWithRetry(() => import("./pages/locations/MoversOakLawn"));
const MoversPalosHills = lazyWithRetry(() => import("./pages/locations/MoversPalosHills"));
const MoversPalosPark = lazyWithRetry(() => import("./pages/locations/MoversPalosPark"));
const MoversTinleyPark = lazyWithRetry(() => import("./pages/locations/MoversTinleyPark"));
const MoversWorth = lazyWithRetry(() => import("./pages/locations/MoversWorth"));
const MoversAlsip = lazyWithRetry(() => import("./pages/locations/MoversAlsip"));
const MoversInverness = lazyWithRetry(() => import("./pages/locations/MoversInverness"));
const MoversJeffersonPark = lazyWithRetry(() => import("./pages/locations/MoversJeffersonPark"));
const MoversOlympiaFields = lazyWithRetry(() => import("./pages/locations/MoversOlympiaFields"));
const MoversPlainfield = lazyWithRetry(() => import("./pages/locations/MoversPlainfield"));
const MoversShorewood = lazyWithRetry(() => import("./pages/locations/MoversShorewood"));
const MoversKankakee = lazyWithRetry(() => import("./pages/locations/MoversKankakee"));
const MoversGlenview = lazyWithRetry(() => import("./pages/locations/MoversGlenview"));
const MoversBartlett = lazyWithRetry(() => import("./pages/locations/MoversBartlett"));
const MoversAlgonquin = lazyWithRetry(() => import("./pages/locations/MoversAlgonquin"));
const MoversBatavia = lazyWithRetry(() => import("./pages/locations/MoversBatavia"));
const MoversBensenville = lazyWithRetry(() => import("./pages/locations/MoversBensenville"));
const MoversBedfordPark = lazyWithRetry(() => import("./pages/locations/MoversBedfordPark"));
const MoversKenilworth = lazyWithRetry(() => import("./pages/locations/MoversKenilworth"));
const MoversCarpentersville = lazyWithRetry(() => import("./pages/locations/MoversCarpentersville"));
const MoversCrete = lazyWithRetry(() => import("./pages/locations/MoversCrete"));
const MoversSouthElgin = lazyWithRetry(() => import("./pages/locations/MoversSouthElgin"));
const MoversPalosHeights = lazyWithRetry(() => import("./pages/locations/MoversPalosHeights"));

// State pages
const MoversTexas = lazyWithRetry(() => import("./pages/states/MoversTexas"));
const MoversCalifornia = lazyWithRetry(() => import("./pages/states/MoversCalifornia"));
const MoversInIllinois = lazyWithRetry(() => import("./pages/MoversInIllinois"));
const NorthCarolinaMovers = lazyWithRetry(() => import("./pages/NorthCarolinaMovers"));

const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const BlogCategory = lazyWithRetry(() => import("./pages/BlogCategory"));
const BlogTag = lazyWithRetry(() => import("./pages/BlogTag"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const FAQCategoryPage = lazyWithRetry(() => import("./pages/faq/FAQCategoryPage"));

// Keyword pages
const BellhopMoversChicago = lazyWithRetry(() => import("./pages/keywords/BellhopMoversChicago"));
const ChicagoMoversReddit = lazyWithRetry(() => import("./pages/keywords/ChicagoMoversReddit"));
const MoverChicago = lazyWithRetry(() => import("./pages/keywords/MoverChicago"));
const MovingCompanyChicagoReviews = lazyWithRetry(() => import("./pages/keywords/MovingCompanyChicagoReviews"));
const MovingCompaniesChicagoReddit = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesChicagoReddit"));
const ChicagoCityMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoCityMovers"));
const ChicagoMoversTheProfessionals = lazyWithRetry(() => import("./pages/keywords/ChicagoMoversTheProfessionals"));
const ChicagoBearsMoving = lazyWithRetry(() => import("./pages/keywords/ChicagoBearsMoving"));
const BestChicagoMovers = lazyWithRetry(() => import("./pages/keywords/BestChicagoMovers"));
const ChicagoMovingCompaniesReviews = lazyWithRetry(() => import("./pages/keywords/ChicagoMovingCompaniesReviews"));
const NapervilleMovingCost = lazyWithRetry(() => import("./pages/keywords/NapervilleMovingCost"));
const LongDistanceMoversNaperville = lazyWithRetry(() => import("./pages/keywords/LongDistanceMoversNaperville"));
const BestMoversNaperville = lazyWithRetry(() => import("./pages/keywords/BestMoversNaperville"));
const ChicagoLocalMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoLocalMovers"));
const MoversNearMeChicago = lazyWithRetry(() => import("./pages/keywords/MoversNearMeChicago"));
const LocalMovingCompaniesChicago = lazyWithRetry(() => import("./pages/keywords/LocalMovingCompaniesChicago"));
const MoversInChicagoArea = lazyWithRetry(() => import("./pages/keywords/MoversInChicagoArea"));
const MovingServicesInChicago = lazyWithRetry(() => import("./pages/keywords/MovingServicesInChicago"));
const MovingAndStorageChicago = lazyWithRetry(() => import("./pages/keywords/MovingAndStorageChicago"));
const MovingLaborChicago = lazyWithRetry(() => import("./pages/keywords/MovingLaborChicago"));
const PremiumMovingChicago = lazyWithRetry(() => import("./pages/keywords/PremiumMovingChicago"));
const FastMoversChicago = lazyWithRetry(() => import("./pages/keywords/FastMoversChicago"));
const SameDayMoversChicago = lazyWithRetry(() => import("./pages/keywords/SameDayMoversChicago"));
const CheapMoversChicago = lazyWithRetry(() => import("./pages/keywords/CheapMoversChicago"));
const ProfessionalMoversChicago = lazyWithRetry(() => import("./pages/keywords/ProfessionalMoversChicago"));
const BestMovingCompaniesChicago = lazyWithRetry(() => import("./pages/keywords/BestMovingCompaniesChicago"));
const MovingCompaniesChicagoCheap = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesChicagoCheap"));
const MoversInChicagoCheap = lazyWithRetry(() => import("./pages/keywords/MoversInChicagoCheap"));
const CheapMovingCompaniesChicago = lazyWithRetry(() => import("./pages/keywords/CheapMovingCompaniesChicago"));
const ChicagoToFloridaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToFloridaMovers"));
const FloridaCorridorPage = lazyWithRetry(() => import("./pages/keywords/FloridaCorridorPage"));

const ChicagoToTennesseeMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToTennesseeMovers"));
const ChicagoToNorthCarolinaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToNorthCarolinaMovers"));
const ChicagoToColoradoMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToColoradoMovers"));
const ChicagoToGeorgiaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToGeorgiaMovers"));
const ChicagoToNevadaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToNevadaMovers"));
const ChicagoToWashingtonMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToWashingtonMovers"));
const ChicagoToMichiganMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToMichiganMovers"));
const ChicagoToMiamiMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToMiamiMovers"));
const ChicagoToOrlandoMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToOrlandoMovers"));
const ChicagoToTampaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToTampaMovers"));
const ChicagoToHoustonMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToHoustonMovers"));
const ChicagoToDallasMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToDallasMovers"));
const ChicagoToAustinMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToAustinMovers"));
const ChicagoToLosAngelesMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToLosAngelesMovers"));
const ChicagoToSanDiegoMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToSanDiegoMovers"));
const ChicagoToPhoenixMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToPhoenixMovers"));
const ChicagoToNashvilleMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToNashvilleMovers"));
const ChicagoToDenverMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToDenverMovers"));
const ChicagoToAtlantaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToAtlantaMovers"));
const ChicagoToOhioMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToOhioMovers"));
const ChicagoToMissouriMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToMissouriMovers"));
const ChicagoToPennsylvaniaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToPennsylvaniaMovers"));
const ChicagoToMassachusettsMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToMassachusettsMovers"));
const ChicagoToMinnesotaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToMinnesotaMovers"));
const AfterHoursMoversChicago = lazyWithRetry(() => import("./pages/keywords/AfterHoursMoversChicago"));
const CheapestMovingCompanyChicago = lazyWithRetry(() => import("./pages/keywords/CheapestMovingCompanyChicago"));
const BestMovingCompanyChicago = lazyWithRetry(() => import("./pages/keywords/BestMovingCompanyChicago"));
const LocalMoversNearMe = lazyWithRetry(() => import("./pages/keywords/LocalMoversNearMe"));
const LocalMoverIL = lazyWithRetry(() => import("./pages/keywords/LocalMoverIL"));
const WhatShouldYouNotMove = lazyWithRetry(() => import("./pages/keywords/WhatShouldYouNotMove"));
const LocalMoverQuotes = lazyWithRetry(() => import("./pages/keywords/LocalMoverQuotes"));
const ChicagoMovingQuotes = lazyWithRetry(() => import("./pages/keywords/ChicagoMovingQuotes"));
const BestLocalMovingCompany = lazyWithRetry(() => import("./pages/keywords/BestLocalMovingCompany"));
const HowManyMoversDoINeed = lazyWithRetry(() => import("./pages/keywords/HowManyMoversDoINeed"));
const HowManyMoversFor2Bedroom = lazyWithRetry(() => import("./pages/keywords/HowManyMoversFor2Bedroom"));
const CanMoversMovePlants = lazyWithRetry(() => import("./pages/keywords/CanMoversMovePlants"));
const CanMoversMoveAlcohol = lazyWithRetry(() => import("./pages/keywords/CanMoversMoveAlcohol"));
const HowDoMoversCharge = lazyWithRetry(() => import("./pages/keywords/HowDoMoversCharge"));
const HowMuchDoMoversCostChicago = lazyWithRetry(() => import("./pages/keywords/HowMuchDoMoversCostChicago"));
const DoMoversTakeCash = lazyWithRetry(() => import("./pages/keywords/DoMoversTakeCash"));
const WhatToGiveMovers = lazyWithRetry(() => import("./pages/keywords/WhatToGiveMovers"));
const MovingCompaniesNearMe = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesNearMe"));
const BestMoversNearMe = lazyWithRetry(() => import("./pages/keywords/BestMoversNearMe"));
const CheapMovingCompaniesNearMe = lazyWithRetry(() => import("./pages/keywords/CheapMovingCompaniesNearMe"));
const HowToPackForAMove = lazyWithRetry(() => import("./pages/keywords/HowToPackForAMove"));
const HowDoesMovingWork = lazyWithRetry(() => import("./pages/keywords/HowDoesMovingWork"));
const LongDistanceMovers = lazyWithRetry(() => import("./pages/keywords/LongDistanceMovers"));
const CrossCountryMovers = lazyWithRetry(() => import("./pages/keywords/CrossCountryMovers"));
const InterstateMovers = lazyWithRetry(() => import("./pages/keywords/InterstateMovers"));
const NationalMovingCompany = lazyWithRetry(() => import("./pages/keywords/NationalMovingCompany"));
const NationwideMovers = lazyWithRetry(() => import("./pages/keywords/NationwideMovers"));
const Movers = lazyWithRetry(() => import("./pages/keywords/Movers"));
const MovingCompanies = lazyWithRetry(() => import("./pages/keywords/MovingCompanies"));
const PackersAndMovers = lazyWithRetry(() => import("./pages/keywords/PackersAndMovers"));
const MoversNearMe = lazyWithRetry(() => import("./pages/keywords/MoversNearMe"));
const MoversOfChicago = lazyWithRetry(() => import("./pages/keywords/MoversOfChicago"));
const MovingCompaniesChicagoArea = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesChicagoArea"));
const MoversLife = lazyWithRetry(() => import("./pages/MoversLife"));
const MovingCompaniesChicagoAreaPillar = lazyWithRetry(() => import("./pages/MovingCompaniesChicagoAreaPillar"));
const VernonHillsMovingPartner = lazyWithRetry(() => import("./pages/keywords/VernonHillsMovingPartner"));
const DownersGroveMovers = lazyWithRetry(() => import("./pages/keywords/DownersGroveMovers"));
const StCharlesMovers = lazyWithRetry(() => import("./pages/keywords/StCharlesMovers"));
const LibertyvilleMovers = lazyWithRetry(() => import("./pages/keywords/LibertyvilleMovers"));
const DeerfieldMovers = lazyWithRetry(() => import("./pages/keywords/DeerfieldMovers"));
const BestTimesToMoveChicago = lazyWithRetry(() => import("./pages/keywords/BestTimesToMoveChicago"));
const WisconsinMovers = lazyWithRetry(() => import("./pages/keywords/WisconsinMovers"));
const TwentyFourHourMoversChicago = lazyWithRetry(() => import("./pages/keywords/TwentyFourHourMoversChicago"));
const LastMinuteMoversChicago = lazyWithRetry(() => import("./pages/keywords/LastMinuteMoversChicago"));
const CommercialMoversNearMe = lazyWithRetry(() => import("./pages/keywords/CommercialMoversNearMe"));
const LongDistanceMoversChicago = lazyWithRetry(() => import("./pages/keywords/LongDistanceMoversChicago"));
const BestLongDistanceMovingCompanies = lazyWithRetry(() => import("./pages/keywords/BestLongDistanceMovingCompanies"));
const SeniorMovers = lazyWithRetry(() => import("./pages/keywords/SeniorMovers"));
const MovingCompaniesInChicagoArea = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesInChicagoArea"));
const PianoMoversKeyword = lazyWithRetry(() => import("./pages/keywords/PianoMovers"));
const FullServiceMovers = lazyWithRetry(() => import("./pages/keywords/FullServiceMovers"));
const OfficeMovers = lazyWithRetry(() => import("./pages/keywords/OfficeMovers"));
const ApartmentMoversChicago = lazyWithRetry(() => import("./pages/keywords/ApartmentMoversChicago"));
const BusinessMovers = lazyWithRetry(() => import("./pages/keywords/BusinessMovers"));
const MovingCompanyChicago = lazyWithRetry(() => import("./pages/keywords/MovingCompanyChicago"));
const MovingServicesChicago = lazyWithRetry(() => import("./pages/keywords/MovingServicesChicago"));
const LongDistanceMovingCompaniesChicago = lazyWithRetry(() => import("./pages/keywords/LongDistanceMovingCompaniesChicago"));
const NapervilleMovingCompany = lazyWithRetry(() => import("./pages/keywords/NapervilleMovingCompany"));
const MovingCompanyChicagoIL = lazyWithRetry(() => import("./pages/keywords/MovingCompanyChicagoIL"));
const ChicagoApartmentMovingCompany = lazyWithRetry(() => import("./pages/keywords/ChicagoApartmentMovingCompany"));
const LocalMovingCompanyChicago = lazyWithRetry(() => import("./pages/keywords/LocalMovingCompanyChicago"));
const InterstateMoversChicago = lazyWithRetry(() => import("./pages/keywords/InterstateMoversChicago"));
const ChicagoToTexasMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToTexasMovers"));
const ChicagoToArizonaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToArizonaMovers"));
const ChicagoToNewYorkMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToNewYorkMovers"));
const OutOfStateMoversChicago = lazyWithRetry(() => import("./pages/keywords/OutOfStateMoversChicago"));
const MoversNational = lazyWithRetry(() => import("./pages/keywords/MoversNational"));
const MoversInChicagoIL = lazyWithRetry(() => import("./pages/keywords/MoversInChicagoIL"));
const LongDistanceMoversBest = lazyWithRetry(() => import("./pages/keywords/LongDistanceMoversBest"));
const MoversChicagoSouthSide = lazyWithRetry(() => import("./pages/keywords/MoversChicagoSouthSide"));
const ResidentialMovingCompaniesChicago = lazyWithRetry(() => import("./pages/keywords/ResidentialMovingCompaniesChicago"));
const MovingCompaniesElmhurstIL = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesElmhurstIL"));
const LakeForestMovers = lazyWithRetry(() => import("./pages/keywords/LakeForestMovers"));
const LifetimeMoversInOakBrookRelocation = lazyWithRetry(() => import("./pages/keywords/LifetimeMoversInOakBrookRelocation"));
const AffordableMovingCompanyChicago = lazyWithRetry(() => import("./pages/keywords/AffordableMovingCompanyChicago"));
const ChicagoSuburbMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoSuburbMovers"));
const MovingCompaniesOakPark = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesOakPark"));
const MoversDownersGroveIL = lazyWithRetry(() => import("./pages/keywords/MoversDownersGroveIL"));
const ChicagoToCaliforniaMovers = lazyWithRetry(() => import("./pages/keywords/ChicagoToCaliforniaMovers"));
const DeKalbMovingCompany = lazyWithRetry(() => import("./pages/keywords/DeKalbMovingCompany"));
const LongDistanceMoversChicagoIL = lazyWithRetry(() => import("./pages/keywords/LongDistanceMoversChicagoIL"));
const LongDistanceMoveChicago = lazyWithRetry(() => import("./pages/keywords/LongDistanceMoveChicago"));
const MovingCompaniesLombardIL = lazyWithRetry(() => import("./pages/keywords/MovingCompaniesLombardIL"));
const CrossCountryMoversChicago = lazyWithRetry(() => import("./pages/keywords/CrossCountryMoversChicago"));
const MoversLombardIL = lazyWithRetry(() => import("./pages/keywords/MoversLombardIL"));
const ExpertMoversChicago = lazyWithRetry(() => import("./pages/keywords/ExpertMoversChicago"));
const MoversInDeKalb = lazyWithRetry(() => import("./pages/keywords/MoversInDeKalb"));
// Location page
const MoversLongGrove = lazyWithRetry(() => import("./pages/locations/MoversLongGrove"));
const MoversStCharlesMO = lazyWithRetry(() => import("./pages/locations/MoversStCharlesMO"));
const MoversStCharles = lazyWithRetry(() => import("./pages/locations/MoversStCharles"));
const MoversCarbondale = lazyWithRetry(() => import("./pages/locations/MoversCarbondale"));
const LongDistanceMoversIllinois = lazyWithRetry(() => import("./pages/keywords/LongDistanceMoversIllinois"));
const RealEstateBrokers = lazyWithRetry(() => import("./pages/RealEstateBrokers"));

// Partner Portal routes moved into a self-contained chunk. See
// src/components/partner/PartnerRoutes.tsx — its 11 lazy() page imports and
// PartnerProtectedRoute/PartnerLayout no longer parse on public marketing
// pages, they only ship when /partner/* is visited.
import { partnerRouteElements } from "./components/partner/PartnerRoutes";

const SignEstimate = lazyWithRetry(() => import("./pages/SignEstimate"));
const CarrierAgreement = lazyWithRetry(() => import("./pages/CarrierAgreement"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const ViewEstimate = lazyWithRetry(() => import("./pages/ViewEstimate"));
const ReviewPage = lazyWithRetry(() => import("./pages/public/ReviewPage"));
const ReviewQuote = lazyWithRetry(() => import("./pages/booking/ReviewQuote"));
const BookingInventory = lazyWithRetry(() => import("./pages/booking/BookingInventory"));
const BookingDetails = lazyWithRetry(() => import("./pages/booking/BookingDetails"));
const ConfirmBooking = lazyWithRetry(() => import("./pages/booking/ConfirmBooking"));
const Insurance = lazyWithRetry(() => import("./pages/booking/Insurance"));
const Review = lazyWithRetry(() => import("./pages/booking/Review"));
const Signature = lazyWithRetry(() => import("./pages/booking/Signature"));
const Payment = lazyWithRetry(() => import("./pages/booking/Payment"));
const Confirmation = lazyWithRetry(() => import("./pages/booking/Confirmation"));
// Admin route elements are imported directly so React Router sees raw <Route> nodes
import { adminRouteElements } from "./components/AdminRoutes";

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
      networkMode: 'online',
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
    mutations: {
      onError: (error) => {
        // Skip logging for chunk/network loading errors (handled by lazyWithRetry)
        if (error instanceof Error && 
            (error.message.includes('Load failed') || 
             error.message.includes('Failed to fetch') ||
             error.message.includes('dynamically imported module'))) {
          console.warn('Network/chunk loading error in mutation:', error.message);
          return;
        }
        
        // Convert Supabase error objects to proper Error instances for Sentry
        let errorToCapture: Error = error instanceof Error ? error : new Error('Unknown error');
        let extraContext: Record<string, unknown> | undefined;
        
        // Only treat as Supabase error if it has Supabase-specific properties (code, details, hint)
        if (error && typeof error === 'object' && 'message' in error && !(error instanceof Error)) {
          const possibleSupabaseError = error as { code?: string; details?: string; hint?: string; message: string };
          // Check for Supabase-specific properties to distinguish from generic errors
          if (possibleSupabaseError.code || possibleSupabaseError.details || possibleSupabaseError.hint) {
            errorToCapture = new Error(possibleSupabaseError.message);
            errorToCapture.name = possibleSupabaseError.code || 'SupabaseError';
            extraContext = {
              code: possibleSupabaseError.code,
              details: possibleSupabaseError.details,
              hint: possibleSupabaseError.hint,
            };
          } else {
            errorToCapture = new Error(possibleSupabaseError.message);
            errorToCapture.name = 'MutationError';
          }
        }
        
        import('@sentry/react').then(S => {
          S.captureException(errorToCapture, {
            tags: { query_error: true },
            extra: extraContext,
          });
        }).catch(() => console.error('Sentry load failed', errorToCapture));
      },
    },
  },
});

// Lightweight error boundary that lazily loads Sentry on error

class LightErrorBoundary extends Component<
  { children: ReactNode; fallback: typeof ErrorFallback },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    import('@sentry/react').then(S => {
      S.withScope((scope) => {
        scope.setContext('React Error Info', { componentStack: errorInfo.componentStack });
        S.captureException(error);
      });
    }).catch(() => console.error('Sentry unavailable', error));
  }
  render() {
    if (this.state.hasError && this.state.error) {
      const Fallback = this.props.fallback;
      return <Fallback error={this.state.error} componentStack="" eventId="" resetError={() => this.setState({ hasError: false, error: null })} />;
    }
    return this.props.children;
  }
}

const App = () => {
  // Email & SMS automation hooks moved to AdminRuntime (only run on /admin routes)
  useEffect(() => { sweepStaleDrafts(); }, []);


  
  return (
    <QueryClientProvider client={queryClient}>
      <LightErrorBoundary fallback={ErrorFallback}>
        <TooltipProvider>
        <Suspense fallback={null}><Toaster /><Sonner /></Suspense>
        <BrowserRouter>
        <ScrollToTop />
        <GTMRouteTracker />
        <MetaPixelRouteTracker />
        <Suspense fallback={null}>
          <GoogleAnalyticsTracker />
          <PageAnalyticsTracker />
        </Suspense>
        {/* Only mount redirect components when URL matches legacy patterns */}
        {window.location.search.includes('elementskit_template') && <ElementsKitRedirect />}
        <TrailingSlashRedirect />
        {(window.location.pathname.includes('/city/') || window.location.pathname.startsWith('/movers-in-') || window.location.pathname.startsWith('/locations/')) && <LegacyPathRedirect />}
        <RedirectHandler>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/former" element={<FormerIndex />} />
          <Route path="/former/:slug" element={<FormerMoverPage />} />
          <Route path="/auto-shipping-quotes" element={<AutoShippingQuotes />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/moving-services" element={<Services />} />
          <Route path="/moving-services/residential" element={<ResidentialMoving />} />
          <Route path="/moving-services/apartment" element={<ApartmentMoving />} />
          <Route path="/moving-services/commercial" element={<CommercialMoving />} />
          <Route path="/moving-services/full-service" element={<FullServiceMoving />} />
          <Route path="/moving-services/household-moving" element={<HouseholdMoving />} />
          <Route path="/moving-services/interstate-move" element={<InterstateMove />} />
          <Route path="/moving-services/local-move" element={<LocalMove />} />
          <Route path="/moving-services/long-distance-move" element={<LongDistanceMove />} />
          {/* Legacy service URL redirects - preserve Google rankings */}
          <Route path="/moving-services/cross-country" element={<CrossCountryMoving />} />
          <Route path="/moving-services/same-day" element={<SameDayMoving />} />
          <Route path="/moving-services/packing-service" element={<PackingService />} />
          <Route path="/moving-services/storage-services" element={<StorageServices />} />
          <Route path="/moving-services/senior-movers" element={<SeniorMoversService />} />
          <Route path="/moving-services/piano-movers" element={<PianoMovers />} />
          <Route path="/moving-services/moving-supplies" element={<MovingSupplies />} />
          <Route path="/moving-services/national-moving" element={<NationalMoving />} />
          <Route path="/moving-services/office-move" element={<OfficeMove />} />
          <Route path="/moving-with-kids-in-chicagoland" element={<MovingWithKids />} />
          <Route path="/moving-services/competitive-pricing" element={<CompetitivePricing />} />
          <Route path="/moving-services/quick-pickup" element={<QuickPickup />} />
          <Route path="/moving-services/emergency-move" element={<EmergencyMove />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/about-us" element={<About />} />

          {/* Old WordPress URLs - Redirects for 404 cleanup */}
          {/* Location variant redirects for 404 cleanup */}
          {/* Trailing slash redirects for existing pages */}
          {/* Soft 404 fixes - January 2026 */}
          {/* Cleaned duplicates - keeping unique redirects only */}
          {/* commercial-movers-near-me and local-movers-near-me redirects REMOVED - keyword pages now serve at these routes */}
          
          {/* Google 404 Fixes - February 2026 */}
          {/* Google Soft 404 Fixes - January 2026 */}
          {/* Crawled Not Indexed Fixes - January 23, 2026 */}
          {/* Chicago neighborhood patterns */}
          {/* Legacy location patterns with -illinois suffix */}
          {/* Location typo fixes */}
          {/* Long distance pattern */}
          {/* Legacy WordPress blog URLs causing crawl issues */}
          {/* Bing Duplicate Meta Description Fix - February 2026 */}
          {/* Office/commercial pattern */}
          {/* WordPress API/Bot URLs - redirect to home */}
          {/* Legacy WordPress blog URLs - redirect to blog */}
          {/* Legacy /locations URL - redirect to service-areas */}
          {/* Fix new-york-city typo - redirect to new-york */}
          {/* Bing 404 Fixes - Redirect incorrect /movers-{city} URL format to correct /service-areas/movers/{city} format */}
          {/* Bing 404 Fixes - Connecticut/Other cities without dedicated pages - redirect to service-areas */}
          {/* Bing Ranked 404 Fixes - Illinois Pickup Areas (January 2026) */}
          {/* Bing Ranked 404 Fixes - California Delivery Cities (January 2026) */}
          {/* Bing Ranked 404 Fix - Guide Redirect (January 2026) */}
          {/* ====== GSC Noindex Cleanup - February 3, 2026 ====== */}
          {/* REMOVED conflicting redirects - these now have dedicated keyword pages at lines 1211+ */}
          {/* Keep only redirects for URLs that genuinely don't need standalone pages */}
          {/* downers-grove-movers now has dedicated keyword page - see routes below */}
          
          {/* Phase 1: Fix dead-end state redirects - GSC reported noindex */}
          {/* Phase 2: Legacy /locations/*-movers redirects - GSC reported noindex */}
          {/* End GSC Noindex Cleanup */}
          
          {/* Streeterville Chicago neighborhood - dedicated page */}
          <Route path="/service-areas/movers/streeterville" element={<MoversStreeterville />} />
          {/* Location Pages */}
          <Route path="/service-areas/movers/anaheim" element={<MoversAnaheim />} />
          <Route path="/service-areas/movers/arlington" element={<MoversArlington />} />
          <Route path="/service-areas/movers/austin" element={<MoversAustin />} />
          <Route path="/service-areas/movers/bakersfield" element={<MoversBakersfield />} />
          <Route path="/service-areas/movers/barrington" element={<MoversBarrington />} />
          <Route path="/service-areas/movers/bucktown" element={<MoversBucktown />} />
          <Route path="/service-areas/movers/avondale" element={<MoversAvondale />} />
          <Route path="/service-areas/movers/loop" element={<MoversLoop />} />
          <Route path="/service-areas/movers/cicero" element={<MoversCicero />} />
          <Route path="/service-areas/movers/ukrainian-village" element={<MoversUkrainianVillage />} />
          <Route path="/service-areas/movers/humboldt-park" element={<MoversHumboldtPark />} />
          <Route path="/service-areas/movers/rogers-park" element={<MoversRogersPark />} />
          <Route path="/service-areas/movers/edgebrook" element={<MoversEdgebrook />} />
          <Route path="/service-areas/movers/wrigleyville" element={<MoversWrigleyville />} />
          <Route path="/service-areas/movers/gurnee" element={<MoversGurnee />} />
          <Route path="/locations/west-loop-il-movers" element={<MoversWestLoop />} />
          <Route path="/service-areas/movers/chicago" element={<MoversChicago />} />
          <Route path="/service-areas/movers/dallas" element={<MoversDallas />} />
          <Route path="/service-areas/movers/los-angeles" element={<MoversLosAngeles />} />
          <Route path="/service-areas/movers/naperville" element={<MoversNaperville />} />
          <Route path="/locations/home-movers-in-naperville-il" element={<Navigate to="/service-areas/movers/naperville" replace />} />
          {/* Legacy WordPress slugs — 301 to canonical (GSC redirect-error fixes) */}
          <Route path="/locations/kildeer-il-movers" element={<Navigate to="/service-areas/movers/kildeer" replace />} />
          <Route path="/locations/movers-in-elmhurst" element={<Navigate to="/service-areas/movers/elmhurst" replace />} />
          <Route path="/your-reliable-moving-partner-in-vernon-hills-il" element={<Navigate to="/service-areas/movers/vernon-hills" replace />} />
          <Route path="/service-areas/movers/wilmette" element={<MoversWilmette />} />
          <Route path="/service-areas/movers/winnetka" element={<MoversWinnetka />} />
          <Route path="/service-areas/movers/evanston" element={<MoversEvanston />} />
          <Route path="/service-areas/movers/oak-park" element={<MoversOakPark />} />
          <Route path="/service-areas/movers/schaumburg" element={<MoversSchaumburg />} />
          <Route path="/service-areas/movers/hoffman-estates" element={<MoversHoffmanEstates />} />
          <Route path="/service-areas/movers/downers-grove" element={<MoversDownersGrove />} />
          <Route path="/service-areas/movers/aurora" element={<MoversAurora />} />
          <Route path="/service-areas/movers/joliet" element={<MoversJoliet />} />
          <Route path="/locations/hinsdale-movers" element={<MoversHinsdale />} />
          <Route path="/locations/lake-barrington-il-movers" element={<MoversLakeBarrington />} />
          <Route path="/service-areas/movers/gold-coast" element={<MoversGoldCoast />} />
          <Route path="/service-areas/movers/rockford" element={<MoversRockford />} />
          <Route path="/service-areas/movers/dekalb" element={<MoversDeKalb />} />
          <Route path="/service-areas/movers/highland-park" element={<MoversHighlandPark />} />
          <Route path="/service-areas/movers/wheaton" element={<MoversWheaton />} />
          <Route path="/service-areas/movers/vernon-hills" element={<MoversVernonHills />} />
          <Route path="/service-areas/movers/northbrook" element={<MoversNorthbrook />} />
          <Route path="/service-areas/movers/lake-forest" element={<MoversLakeForest />} />
          <Route path="/locations/lincoln-park-chicago-movers" element={<MoversLincolnPark />} />
          <Route path="/service-areas/movers/geneva" element={<MoversGeneva />} />
          <Route path="/service-areas/movers/elmhurst" element={<MoversElmhurst />} />
          <Route path="/movers-in-northfield-illinois" element={<MoversNorthfield />} />
          <Route path="/service-areas/movers/lakeview" element={<MoversLakeview />} />
          <Route path="/service-areas/movers/old-town" element={<MoversOldTown />} />
          <Route path="/service-areas/movers/deer-park" element={<MoversDeerPark />} />
          <Route path="/service-areas/movers/kildeer" element={<MoversKildeer />} />
          <Route path="/service-areas/movers/homer-glen" element={<MoversHomerGlen />} />
          <Route path="/oak-brook-movers-illinois" element={<MoversOakBrook />} />
          <Route path="/service-areas/movers/oakbrook-terrace" element={<MoversOakbrookTerrace />} />
          <Route path="/service-areas/movers/villa-park" element={<MoversVillaPark />} />
          <Route path="/service-areas/movers/westmont" element={<MoversWestmont />} />
          <Route path="/service-areas/movers/willowbrook" element={<MoversWillowbrook />} />
          <Route path="/service-areas/movers/lisle" element={<MoversLisle />} />
          <Route path="/service-areas/movers/clarendon-hills" element={<MoversClarendonHills />} />
          <Route path="/service-areas/movers/brookfield" element={<MoversBrookfield />} />
          <Route path="/service-areas/movers/berwyn" element={<MoversBerwyn />} />
          <Route path="/service-areas/movers/riverside" element={<MoversRiverside />} />
          <Route path="/service-areas/movers/quincy" element={<MoversQuincy />} />
          <Route path="/service-areas/movers/forest-park" element={<MoversForestPark />} />
          <Route path="/service-areas/movers/carol-stream" element={<MoversCarolStream />} />
          <Route path="/service-areas/movers/addison" element={<MoversAddison />} />
          <Route path="/service-areas/movers/itasca" element={<MoversItasca />} />
          <Route path="/service-areas/movers/roselle" element={<MoversRoselle />} />
          <Route path="/service-areas/movers/darien" element={<MoversDarien />} />
          <Route path="/service-areas/movers/elk-grove-village" element={<MoversElkGroveVillage />} />
          <Route path="/service-areas/movers/des-plaines" element={<MoversDesPlaines />} />
          <Route path="/service-areas/movers/mount-prospect" element={<MoversMountProspect />} />
          <Route path="/service-areas/movers/arlington-heights" element={<MoversArlingtonHeights />} />
          <Route path="/service-areas/movers/skokie" element={<MoversSkokie />} />
          <Route path="/service-areas/movers/orland-park" element={<MoversOrlandPark />} />
          <Route path="/service-areas/movers/south-loop" element={<MoversSouthLoop />} />
          <Route path="/service-areas/movers/lombard" element={<MoversLombard />} />
          <Route path="/service-areas/movers/st-charles" element={<MoversStCharles />} />
          <Route path="/service-areas/movers/deerfield" element={<MoversDeerfield />} />
          <Route path="/service-areas/movers/indianapolis" element={<MoversIndianapolis />} />
          <Route path="/service-areas/movers/fort-wayne" element={<MoversFortWayne />} />
          <Route path="/service-areas/movers/carmel" element={<MoversCarmel />} />
          <Route path="/service-areas/movers/fishers" element={<MoversFishers />} />
          <Route path="/service-areas/movers/bloomington" element={<MoversBloomington />} />
          <Route path="/service-areas/movers/south-bend" element={<MoversSouthBend />} />
          <Route path="/service-areas/movers/evansville" element={<MoversEvansville />} />
          <Route path="/service-areas/movers/hammond" element={<MoversHammond />} />
          <Route path="/service-areas/movers/gary" element={<MoversGary />} />
          <Route path="/service-areas/movers/lafayette" element={<MoversLafayette />} />
          <Route path="/service-areas/movers/milwaukee" element={<MoversMilwaukee />} />
          <Route path="/service-areas/movers/madison" element={<MoversMadison />} />
          <Route path="/service-areas/movers/green-bay" element={<MoversGreenBay />} />
          <Route path="/service-areas/movers/kenosha" element={<MoversKenosha />} />
          <Route path="/service-areas/movers/racine" element={<MoversRacine />} />
          <Route path="/service-areas/movers/appleton" element={<MoversAppleton />} />
          <Route path="/service-areas/movers/waukesha" element={<MoversWaukesha />} />
          <Route path="/service-areas/movers/eau-claire" element={<MoversEauClaire />} />
          <Route path="/service-areas/movers/detroit" element={<MoversDetroit />} />
          <Route path="/service-areas/movers/grand-rapids" element={<MoversGrandRapids />} />
          <Route path="/service-areas/movers/ann-arbor" element={<MoversAnnArbor />} />
          <Route path="/service-areas/movers/lansing" element={<MoversLansing />} />
          <Route path="/service-areas/movers/sterling-heights" element={<MoversSterlingHeights />} />
          <Route path="/service-areas/movers/flint" element={<MoversFlint />} />
          <Route path="/service-areas/movers/traverse-city" element={<MoversTraverseCity />} />
          <Route path="/service-areas/movers/kalamazoo" element={<MoversKalamazoo />} />
          <Route path="/service-areas/movers/houston" element={<MoversHouston />} />
          <Route path="/service-areas/movers/san-antonio" element={<MoversSanAntonio />} />
          <Route path="/service-areas/movers/san-diego" element={<MoversSanDiego />} />
          <Route path="/service-areas/movers/san-francisco" element={<MoversSanFrancisco />} />
          <Route path="/service-areas/movers/irvine" element={<MoversIrvine />} />
          <Route path="/service-areas/movers/fairfield" element={<MoversFairfield />} />
          <Route path="/service-areas/movers/lubbock" element={<MoversLubbock />} />
          <Route path="/service-areas/movers/san-jose" element={<MoversSanJose />} />
          <Route path="/service-areas/movers/sacramento" element={<MoversSacramento />} />
          <Route path="/service-areas/movers/fresno" element={<MoversFresno />} />
          <Route path="/service-areas/movers/atlanta" element={<MoversAtlanta />} />
          <Route path="/service-areas/movers/glen-ellyn" element={<MoversGlenEllyn />} />
          <Route path="/service-areas/movers/charlotte" element={<MoversCharlotte />} />
          <Route path="/service-areas/movers/raleigh" element={<MoversRaleigh />} />
          <Route path="/service-areas/movers/miami" element={<MoversMiami />} />
          <Route path="/service-areas/movers/tampa" element={<MoversTampa />} />
          <Route path="/service-areas/movers/orlando" element={<MoversOrlando />} />
          <Route path="/service-areas/movers/jacksonville" element={<MoversJacksonville />} />
          <Route path="/service-areas/movers/nashville" element={<MoversNashville />} />
          <Route path="/service-areas/movers/memphis" element={<MoversMemphis />} />
          <Route path="/service-areas/movers/louisville" element={<MoversLouisville />} />
          <Route path="/service-areas/movers/columbus" element={<MoversColumbus />} />
          <Route path="/service-areas/movers/cleveland" element={<MoversCleveland />} />
          <Route path="/service-areas/movers/cincinnati" element={<MoversCincinnati />} />
          <Route path="/service-areas/movers/pittsburgh" element={<MoversPittsburgh />} />
          <Route path="/service-areas/movers/philadelphia" element={<MoversPhiladelphia />} />
          <Route path="/service-areas/movers/boston" element={<MoversBoston />} />
          <Route path="/service-areas/movers/new-york" element={<MoversNewYork />} />
          <Route path="/service-areas/movers/baltimore" element={<MoversBaltimore />} />
          <Route path="/service-areas/movers/minneapolis" element={<MoversMinneapolis />} />
          <Route path="/service-areas/movers/st-louis" element={<MoversStLouis />} />
          <Route path="/service-areas/movers/kansas-city" element={<MoversKansasCity />} />
          <Route path="/service-areas/movers/omaha" element={<MoversOmaha />} />
          <Route path="/service-areas/movers/oklahoma-city" element={<MoversOklahomaCity />} />
          <Route path="/service-areas/movers/tulsa" element={<MoversTulsa />} />
          <Route path="/service-areas/movers/albuquerque" element={<MoversAlbuquerque />} />
          <Route path="/service-areas/movers/tucson" element={<MoversTucson />} />
          <Route path="/service-areas/movers/phoenix" element={<MoversPhoenix />} />
          <Route path="/service-areas/movers/seattle" element={<MoversSeattle />} />
          <Route path="/service-areas/movers/denver" element={<MoversDenver />} />
          <Route path="/service-areas/movers/portland" element={<MoversPortland />} />
          <Route path="/service-areas/movers/las-vegas" element={<MoversLasVegas />} />
          <Route path="/service-areas/movers/long-grove" element={<MoversLongGrove />} />
          <Route path="/service-areas/movers/country-club-hills" element={<MoversCountryClubHills />} />
          <Route path="/service-areas/movers/park-ridge" element={<MoversParkRidge />} />
          <Route path="/service-areas/movers/albany-park" element={<MoversAlbanyPark />} />
          <Route path="/service-areas/movers/streamwood" element={<MoversStreamwood />} />
          <Route path="/service-areas/movers/river-grove" element={<MoversRiverGrove />} />
          <Route path="/service-areas/movers/river-forest" element={<MoversRiverForest />} />
          <Route path="/service-areas/movers/palatine" element={<MoversPalatine />} />
          <Route path="/service-areas/movers/norwood-park" element={<MoversNorwoodPark />} />
          <Route path="/service-areas/movers/minooka" element={<MoversMinooka />} />
          <Route path="/service-areas/movers/woodridge" element={<MoversWoodridge />} />
          <Route path="/service-areas/movers/logan-square" element={<MoversLoganSquare />} />
          <Route path="/service-areas/movers/bolingbrook" element={<MoversBolingbrook />} />
          <Route path="/service-areas/movers/channahon" element={<MoversChannahon />} />
          <Route path="/service-areas/movers/north-chicago" element={<MoversNorthChicago />} />
          <Route path="/service-areas/movers/libertyville" element={<MoversLibertyville />} />
          <Route path="/service-areas/movers/hawthorn-woods" element={<MoversHawthornWoods />} />
          <Route path="/service-areas/movers/monee" element={<MoversMonee />} />
          <Route path="/service-areas/movers/beecher" element={<MoversBeecher />} />
          <Route path="/service-areas/movers/hodgkins" element={<MoversHodgkins />} />
          <Route path="/service-areas/movers/pilsen" element={<MoversPilsen />} />
          <Route path="/service-areas/movers/rolling-meadows" element={<MoversRollingMeadows />} />
          <Route path="/service-areas/movers/mchenry" element={<MoversMcHenry />} />
          <Route path="/service-areas/movers/morris" element={<MoversMorris />} />
          <Route path="/service-areas/movers/bridgeview" element={<MoversBridgeview />} />
          <Route path="/service-areas/movers/burr-ridge" element={<MoversBurrRidge />} />
          <Route path="/service-areas/movers/crestwood" element={<MoversCrestwood />} />
          <Route path="/service-areas/movers/hazel-crest" element={<MoversHazelCrest />} />
          <Route path="/service-areas/movers/hickory-hills" element={<MoversHickoryHills />} />
          <Route path="/service-areas/movers/hometown" element={<MoversHometown />} />
          <Route path="/service-areas/movers/justice" element={<MoversJustice />} />
          <Route path="/service-areas/movers/oak-lawn" element={<MoversOakLawn />} />
          <Route path="/service-areas/movers/palos-hills" element={<MoversPalosHills />} />
          <Route path="/service-areas/movers/palos-park" element={<MoversPalosPark />} />
          <Route path="/service-areas/movers/tinley-park" element={<MoversTinleyPark />} />
          <Route path="/service-areas/movers/worth" element={<MoversWorth />} />
          <Route path="/service-areas/movers/alsip" element={<MoversAlsip />} />
          <Route path="/service-areas/movers/inverness" element={<MoversInverness />} />
          <Route path="/service-areas/movers/jefferson-park" element={<MoversJeffersonPark />} />
          <Route path="/service-areas/movers/olympia-fields" element={<MoversOlympiaFields />} />
          <Route path="/service-areas/movers/plainfield" element={<MoversPlainfield />} />
          <Route path="/service-areas/movers/shorewood" element={<MoversShorewood />} />
          <Route path="/service-areas/movers/kankakee" element={<MoversKankakee />} />
          <Route path="/service-areas/movers/glenview" element={<MoversGlenview />} />
          <Route path="/service-areas/movers/bartlett" element={<MoversBartlett />} />
          <Route path="/service-areas/movers/algonquin" element={<MoversAlgonquin />} />
          <Route path="/service-areas/movers/batavia" element={<MoversBatavia />} />
          <Route path="/service-areas/movers/bensenville" element={<MoversBensenville />} />
          <Route path="/service-areas/movers/bedford-park" element={<MoversBedfordPark />} />
          <Route path="/service-areas/movers/kenilworth" element={<MoversKenilworth />} />
          <Route path="/service-areas/movers/carpentersville" element={<MoversCarpentersville />} />
          <Route path="/service-areas/movers/crete" element={<MoversCrete />} />
          <Route path="/service-areas/movers/south-elgin" element={<MoversSouthElgin />} />
          <Route path="/service-areas/movers/palos-heights" element={<MoversPalosHeights />} />
          <Route path="/service-areas/movers/western-springs" element={<MoversWesternSprings />} />
          <Route path="/service-areas/movers/lemont" element={<MoversLemont />} />
          <Route path="/service-areas/movers/pingree-grove" element={<MoversPingreeGrove />} />
          <Route path="/service-areas/movers/glencoe" element={<MoversGlencoe />} />
          <Route path="/service-areas/movers/lincolnshire" element={<MoversLincolnshire />} />
          
          {/* State-Level Landing Pages */}
          <Route path="/movers/texas" element={<MoversTexas />} />
          <Route path="/movers/california" element={<MoversCalifornia />} />
          <Route path="/movers-in-illinois" element={<MoversInIllinois />} />
          <Route path="/north-carolina-movers" element={<NorthCarolinaMovers />} />
          {/* Keyword Pages */}
          <Route path="/bellhop-movers-chicago" element={<BellhopMoversChicago />} />
          <Route path="/chicago-movers-reddit" element={<ChicagoMoversReddit />} />
          <Route path="/mover-chicago" element={<MoverChicago />} />
          <Route path="/moving-company-chicago-reviews" element={<MovingCompanyChicagoReviews />} />
          <Route path="/moving-companies-chicago-reddit" element={<MovingCompaniesChicagoReddit />} />
          <Route path="/chicago-city-movers" element={<ChicagoCityMovers />} />
          <Route path="/chicago-movers-the-professionals" element={<ChicagoMoversTheProfessionals />} />
          <Route path="/chicago-bears-moving" element={<ChicagoBearsMoving />} />
          <Route path="/best-chicago-movers" element={<BestChicagoMovers />} />
          <Route path="/chicago-moving-companies-reviews" element={<ChicagoMovingCompaniesReviews />} />
          <Route path="/chicago-local-movers" element={<ChicagoLocalMovers />} />
          <Route path="/movers-near-me-chicago" element={<MoversNearMeChicago />} />
          <Route path="/local-moving-companies-chicago" element={<LocalMovingCompaniesChicago />} />
          <Route path="/movers-in-chicago-area" element={<MoversInChicagoArea />} />
          <Route path="/moving-services-in-chicago" element={<MovingServicesInChicago />} />
          <Route path="/moving-and-storage-chicago" element={<MovingAndStorageChicago />} />
          <Route path="/moving-labor-chicago" element={<MovingLaborChicago />} />
          <Route path="/premium-moving-chicago" element={<PremiumMovingChicago />} />
<Route path="/fast-movers-chicago" element={<FastMoversChicago />} />
          <Route path="/same-day-movers-chicago" element={<SameDayMoversChicago />} />
          <Route path="/cheap-movers-chicago" element={<CheapMoversChicago />} />
          <Route path="/professional-movers-chicago" element={<ProfessionalMoversChicago />} />
          <Route path="/best-moving-companies-chicago" element={<BestMovingCompaniesChicago />} />
          <Route path="/moving-companies-chicago-cheap" element={<MovingCompaniesChicagoCheap />} />
          <Route path="/cheap-moving-companies-chicago" element={<CheapMovingCompaniesChicago />} />
          <Route path="/cheapest-moving-company-chicago" element={<CheapestMovingCompanyChicago />} />
          <Route path="/best-moving-company-chicago" element={<BestMovingCompanyChicago />} />
          <Route path="/local-movers-near-me" element={<LocalMoversNearMe />} />
          <Route path="/local-mover-il" element={<LocalMoverIL />} />
          <Route path="/what-should-you-not-move" element={<WhatShouldYouNotMove />} />
          <Route path="/chicago-moving-quotes" element={<ChicagoMovingQuotes />} />
          <Route path="/best-local-moving-company" element={<BestLocalMovingCompany />} />
          <Route path="/how-many-movers-do-i-need" element={<HowManyMoversDoINeed />} />
          <Route path="/how-many-movers-do-i-need-for-a-2-bedroom-apartment" element={<HowManyMoversFor2Bedroom />} />
          <Route path="/can-movers-move-plants" element={<CanMoversMovePlants />} />
          <Route path="/can-movers-move-alcohol" element={<CanMoversMoveAlcohol />} />
          <Route path="/how-do-movers-charge" element={<HowDoMoversCharge />} />
          <Route path="/how-much-do-movers-cost-in-chicago" element={<HowMuchDoMoversCostChicago />} />
          <Route path="/do-movers-take-cash" element={<DoMoversTakeCash />} />
          <Route path="/what-to-give-movers" element={<WhatToGiveMovers />} />
          <Route path="/long-distance-movers" element={<LongDistanceMovers />} />
          <Route path="/cross-country-movers" element={<CrossCountryMovers />} />
          <Route path="/interstate-movers" element={<InterstateMovers />} />
          <Route path="/national-moving-company" element={<NationalMovingCompany />} />
          <Route path="/nationwide-movers" element={<NationwideMovers />} />
          <Route path="/movers" element={<Movers />} />
          <Route path="/moving-companies" element={<MovingCompanies />} />
          <Route path="/packers-and-movers" element={<PackersAndMovers />} />
          <Route path="/movers-near-me" element={<MoversNearMe />} />
          <Route path="/movers-of-chicago" element={<MoversOfChicago />} />
          <Route path="/moving-companies-chicago-area" element={<MovingCompaniesChicagoArea />} />
          <Route path="/your-reliable-moving-partner-in-vernon-hills-il" element={<VernonHillsMovingPartner />} />
          {/* SEO Fix: Redirect to authoritative location page to fix cannibalization */}
          {/* St. Charles SEO consolidated to location page */}
          <Route path="/movers-in-st-charles-illinois" element={<StCharlesMovers />} />
          <Route path="/movers-in-libertyville-il" element={<LibertyvilleMovers />} />
          <Route path="/movers-in-deerfield-il" element={<DeerfieldMovers />} />
          <Route path="/best-times-of-the-year-to-move-in-chicago" element={<BestTimesToMoveChicago />} />
          <Route path="/wisconsin-movers" element={<WisconsinMovers />} />
          <Route path="/commercial-movers-near-me" element={<CommercialMoversNearMe />} />
          <Route path="/long-distance-movers-chicago" element={<LongDistanceMoversChicago />} />
          <Route path="/best-long-distance-moving-companies" element={<BestLongDistanceMovingCompanies />} />
          <Route path="/moving-companies-in-chicago-area" element={<MovingCompaniesInChicagoArea />} />
          {/* GSC 2026-05-11: keyword duplicates 301 to canonical /moving-services/* via redirectMap */}
          <Route path="/office-movers" element={<OfficeMovers />} />
          <Route path="/moving-company-chicago" element={<MovingCompanyChicago />} />
          <Route path="/moving-services-chicago" element={<MovingServicesChicago />} />
          <Route path="/long-distance-moving-companies-chicago" element={<LongDistanceMovingCompaniesChicago />} />
          <Route path="/naperville-moving-company" element={<NapervilleMovingCompany />} />
          <Route path="/naperville-moving-cost" element={<NapervilleMovingCost />} />
          <Route path="/long-distance-movers-naperville" element={<LongDistanceMoversNaperville />} />
          <Route path="/best-movers-naperville" element={<BestMoversNaperville />} />
          <Route path="/moving-company-chicago-il" element={<MovingCompanyChicagoIL />} />
          <Route path="/chicago-apartment-moving-company" element={<ChicagoApartmentMovingCompany />} />
          <Route path="/local-moving-company-chicago" element={<LocalMovingCompanyChicago />} />
          <Route path="/service-areas/movers/st-charles-mo" element={<MoversStCharlesMO />} />
          <Route path="/chicago-to-florida-movers" element={<ChicagoToFloridaMovers />} />
          {FLORIDA_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {TEXAS_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {CALIFORNIA_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {COLORADO_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {ARIZONA_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {NEW_YORK_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {SUBURB_CORRIDOR_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          {DESTINATION_GUIDE_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<FloridaCorridorPage slug={s} />} />))}
          <Route path="/chicago-to-tennessee-movers" element={<ChicagoToTennesseeMovers />} />
          <Route path="/chicago-to-north-carolina-movers" element={<ChicagoToNorthCarolinaMovers />} />
          <Route path="/chicago-to-colorado-movers" element={<ChicagoToColoradoMovers />} />
          <Route path="/chicago-to-georgia-movers" element={<ChicagoToGeorgiaMovers />} />
          <Route path="/chicago-to-nevada-movers" element={<ChicagoToNevadaMovers />} />
          <Route path="/chicago-to-washington-movers" element={<ChicagoToWashingtonMovers />} />
          <Route path="/chicago-to-michigan-movers" element={<ChicagoToMichiganMovers />} />
          <Route path="/chicago-to-miami-movers" element={<ChicagoToMiamiMovers />} />
          <Route path="/chicago-to-orlando-movers" element={<ChicagoToOrlandoMovers />} />
          <Route path="/chicago-to-tampa-movers" element={<ChicagoToTampaMovers />} />
          <Route path="/chicago-to-houston-movers" element={<ChicagoToHoustonMovers />} />
          <Route path="/chicago-to-dallas-movers" element={<ChicagoToDallasMovers />} />
          <Route path="/chicago-to-austin-movers" element={<ChicagoToAustinMovers />} />
          <Route path="/chicago-to-los-angeles-movers" element={<ChicagoToLosAngelesMovers />} />
          <Route path="/chicago-to-san-diego-movers" element={<ChicagoToSanDiegoMovers />} />
          <Route path="/chicago-to-phoenix-movers" element={<ChicagoToPhoenixMovers />} />
          <Route path="/chicago-to-nashville-movers" element={<ChicagoToNashvilleMovers />} />
          <Route path="/chicago-to-denver-movers" element={<ChicagoToDenverMovers />} />
          <Route path="/chicago-to-atlanta-movers" element={<ChicagoToAtlantaMovers />} />
          <Route path="/chicago-to-ohio-movers" element={<ChicagoToOhioMovers />} />
          <Route path="/chicago-to-missouri-movers" element={<ChicagoToMissouriMovers />} />
          <Route path="/chicago-to-pennsylvania-movers" element={<ChicagoToPennsylvaniaMovers />} />
          <Route path="/chicago-to-massachusetts-movers" element={<ChicagoToMassachusettsMovers />} />
          <Route path="/chicago-to-minnesota-movers" element={<ChicagoToMinnesotaMovers />} />
          <Route path="/after-hours-movers-chicago" element={<AfterHoursMoversChicago />} />
          <Route path="/interstate-movers-chicago" element={<InterstateMoversChicago />} />
          <Route path="/chicago-to-texas-movers" element={<ChicagoToTexasMovers />} />
          <Route path="/chicago-to-arizona-movers" element={<ChicagoToArizonaMovers />} />
          <Route path="/chicago-to-new-york-movers" element={<ChicagoToNewYorkMovers />} />
          <Route path="/chicago-suburb-movers" element={<ChicagoSuburbMovers />} />
          <Route path="/out-of-state-movers-chicago" element={<OutOfStateMoversChicago />} />
          <Route path="/movers-national" element={<MoversNational />} />
          <Route path="/lifetime-movers-in-oak-brook-relocation" element={<LifetimeMoversInOakBrookRelocation />} />
          <Route path="/affordable-moving-company-chicago" element={<AffordableMovingCompanyChicago />} />
          <Route path="/affordable-movers-chicago" element={<Navigate to="/affordable-moving-company-chicago" replace />} />
          
          {/* New SEO Keyword Pages - Google Recommendations March 2026 */}
          <Route path="/moving-companies-oak-park" element={<MovingCompaniesOakPark />} />
          <Route path="/movers-downers-grove-il" element={<MoversDownersGroveIL />} />
          <Route path="/chicago-to-california-movers" element={<ChicagoToCaliforniaMovers />} />
          <Route path="/dekalb-moving-company" element={<DeKalbMovingCompany />} />
          
          {/* New SEO Keyword Pages - Google Recommendations Batch 2 March 2026 */}
          <Route path="/long-distance-movers-chicago-il" element={<LongDistanceMoversChicagoIL />} />
          <Route path="/moving-companies-lombard-il" element={<MovingCompaniesLombardIL />} />
          <Route path="/downers-grove-movers" element={<DownersGroveMovers />} />
          <Route path="/cross-country-movers-chicago" element={<CrossCountryMoversChicago />} />
          <Route path="/movers-in-dekalb" element={<MoversInDeKalb />} />

          {/* New SEO Keyword Pages - Google Recommendations Batch 3 March 2026 */}
          <Route path="/movers-lombard-il" element={<MoversLombardIL />} />
          <Route path="/expert-movers-chicago" element={<ExpertMoversChicago />} />
          {/* New SEO Keyword Pages - Google Recommendations Batch 4 March 2026 */}
          <Route path="/movers-in-chicago-il" element={<MoversInChicagoIL />} />
          <Route path="/long-distance-movers-best" element={<LongDistanceMoversBest />} />
          {/* New SEO Keyword Pages - Google Recommendations Batch 5 March 2026 */}
          <Route path="/movers-chicago-south-side" element={<MoversChicagoSouthSide />} />
          <Route path="/residential-moving-companies-chicago" element={<ResidentialMovingCompaniesChicago />} />
          {/* New SEO Keyword Pages - Long Distance Chicago Push March 2026 */}
          <Route path="/long-distance-move-chicago" element={<LongDistanceMoveChicago />} />

          {/* New SEO Keyword Pages - Google Recommendations Batch 7 March 2026 */}
          <Route path="/long-distance-movers-illinois" element={<LongDistanceMoversIllinois />} />
          <Route path="/service-areas/movers/carbondale" element={<MoversCarbondale />} />
          {/* New SEO Keyword Pages - Google Recommendations Batch 6 March 2026 */}
          <Route path="/moving-companies-elmhurst-il" element={<MovingCompaniesElmhurstIL />} />
          <Route path="/lake-forest-movers" element={<LakeForestMovers />} />
          
          {/* New SEO Keyword Pages - Phase 4B */}
          <Route path="/moving-companies-near-me" element={<MovingCompaniesNearMe />} />
          <Route path="/best-movers-near-me" element={<BestMoversNearMe />} />
          <Route path="/cheap-moving-companies-near-me" element={<CheapMovingCompaniesNearMe />} />
          <Route path="/how-to-pack-for-a-move" element={<HowToPackForAMove />} />
          <Route path="/how-does-moving-work" element={<HowDoesMovingWork />} />
          
          {/* SEO Keyword Redirects - Phase 4B */}
          <Route path="/apartment-movers-chicago" element={<ApartmentMoversChicago />} />
          <Route path="/business-movers" element={<BusinessMovers />} />
          
          {/* Budget Movers Keyword Pages */}
          <Route path="/movers-in-chicago-cheap" element={<MoversInChicagoCheap />} />
          
          {/* New SEO Keyword Pages - April 2026 Urgency Cluster */}
          <Route path="/24-hour-movers-chicago" element={<TwentyFourHourMoversChicago />} />
          <Route path="/last-minute-movers-chicago" element={<LastMinuteMoversChicago />} />
          {/* Duplicate /same-day-movers-chicago route removed - actual page component at line 1166 */}
          
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/blog/category/:slug" element={<BlogCategory />} />
          <Route path="/blog/tag/:slug" element={<BlogTag />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/faq/:categorySlug" element={<FAQCategoryPage />} />
          <Route path="/lifetime-movers-reviews" element={<LifetimeMoversReviews />} />
          <Route path="/lifetime-moving-company" element={<LifetimeMovingCompany />} />
          <Route path="/lifetime-moving" element={<LifetimeMoving />} />
          <Route path="/lifetime-moving-and-storage" element={<LifetimeMovingAndStorage />} />
          <Route path="/lifetime-moving-co" element={<LifetimeMovingCo />} />
          <Route path="/about-lifetime-movers" element={<AboutLifetimeMovers />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/real-estate-brokers" element={<RealEstateBrokers />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/sign/:vbNumber" element={<SignEstimate />} />
          <Route path="/carrier-agreement/:token" element={<CarrierAgreement />} />
          <Route path="/view-estimate" element={<ViewEstimate />} />
          <Route path="/review/:vbNumber" element={<ReviewPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/resume-booking" element={<ResumeBooking />} />
          <Route path="/job-inventory" element={<CustomerJobInventory />} />
          <Route path="/customer-payment/:token" element={<CustomerPayment />} />
          <Route path="/booking/:bookingId" element={<BookingProviderWrapper />}>
              <Route path="quote-review" element={<ReviewQuote />} />
              <Route path="inventory" element={<BookingInventory />} />
              <Route path="details" element={<BookingDetails />} />
              <Route path="confirm" element={<ConfirmBooking />} />
              <Route path="insurance" element={<Insurance />} />
              <Route path="review" element={<Review />} />
              <Route path="signature" element={<Signature />} />
              <Route path="payment" element={<Payment />} />
              <Route path="confirmation" element={<Confirmation />} />
            </Route>
            <Route path="/auth" element={<Auth />} />
            <Route path="/portal" element={<CustomerPortal />} />
            <Route path="/admin" element={<ProtectedRoute><CRMPanelWrapper><><AdminLayout /><AdminRuntime /></></CRMPanelWrapper></ProtectedRoute>}>
              {adminRouteElements}
            </Route>
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          {/* Partner Portal Routes (moved to components/partner/PartnerRoutes.tsx) */}
          {partnerRouteElements}
          <Route path="/movers-life" element={<MoversLife />} />
          <Route path="/moving-companies-chicago-area" element={<MovingCompaniesChicagoAreaPillar />} />
          
          {/* Data-driven fallback for any service-area slug not hard-mounted above.
              react-router v6 prefers static segments, so every existing LT route
              still wins on match specificity — this only serves new slugs added
              to the active client's location registry. */}
          <Route path="/service-areas/movers/:slug" element={<LocationPage />} />

          <Route path="/wp-json/*" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <VisitorPresenceTracker />
        <ChatWidgetWrapper />
        <GlobalMobileCTA />
        <ExitIntentOffer />
        <ClickToCallTracker />
        </RedirectHandler>
      </BrowserRouter>

      </TooltipProvider>
    </LightErrorBoundary>
  </QueryClientProvider>
);
};

export default App;
